import { useCallback, useEffect, useRef, useState } from "react";
import { STEP_NAME } from "../onboardingShared";

/** Voice input for the onboarding chat composer, with a prototype fallback. */
export function useChatVoice({ activeSuggestions, nameSuggestion, setDraft, step }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const voiceTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (voiceTimerRef.current) window.clearTimeout(voiceTimerRef.current);
      try {
        recognitionRef.current?.stop?.();
      } catch {
        // ignore
      }
    },
    []
  );

  const stopVoice = useCallback(() => {
    setListening(false);
    if (voiceTimerRef.current) {
      window.clearTimeout(voiceTimerRef.current);
      voiceTimerRef.current = null;
    }
    try {
      recognitionRef.current?.stop?.();
    } catch {
      // ignore
    }
  }, []);

  const simulateVoiceTranscript = useCallback(() => {
    const first = activeSuggestions[0];
    const label =
      typeof first === "string"
        ? first
        : first?.label || (step === STEP_NAME ? nameSuggestion : "California");
    setDraft(label);
    setListening(false);
  }, [activeSuggestions, nameSuggestion, setDraft, step]);

  const handleToggleVoice = useCallback(() => {
    if (listening) {
      stopVoice();
      return;
    }

    const SpeechRecognition =
      typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.onresult = (event) => {
          const transcript = event.results?.[0]?.[0]?.transcript || "";
          if (transcript) setDraft(transcript);
          setListening(false);
        };
        recognition.onerror = () => {
          simulateVoiceTranscript();
        };
        recognition.onend = () => {
          setListening(false);
        };
        recognitionRef.current = recognition;
        setListening(true);
        recognition.start();
        return;
      } catch {
        // Fall through to simulation.
      }
    }

    // Prototype fallback when SpeechRecognition is unavailable.
    setListening(true);
    voiceTimerRef.current = window.setTimeout(() => {
      simulateVoiceTranscript();
    }, 1400);
  }, [listening, setDraft, simulateVoiceTranscript, stopVoice]);

  return { listening, stopVoice, handleToggleVoice };
}
